import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { stylingApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { formatDate, formatDuration } from '../lib/formatters.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business Styling Consoleタイミング分析。全体/ステップ別(平均/P50/P90)タイミングと日次トレンドを可視化
 * @graph-connects stylingApi [calls] Styling APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects formatDate [calls] 日付フォーマット
 * @graph-connects formatDuration [calls] 時間フォーマット
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 */
export const Route = createFileRoute('/styling/timing')({
  component: StylingTimingPage,
});

/** @graph-connects none */
const STEP_LABELS: Record<string, string> = {
  screen_rendered: '画面表示',
  filter_applied: 'フィルター適用',
  item_added_to_coordinate: 'アイテム追加',
  styling_submitted: 'スタイリング送信',
};

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function StylingTimingPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');

  const timingQuery = useQuery({
    queryKey: queryKeys.styling.sessionTiming(startDate, endDate),
    queryFn: () => stylingApi.getSessionTiming(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const data = timingQuery.data;

  const stepChartData =
    data?.steps.map((s) => ({
      step: STEP_LABELS[s.step] ?? s.step,
      avgMs: s.avgMs !== null ? Math.round(s.avgMs) : null,
      p50Ms: s.p50Ms !== null ? Math.round(s.p50Ms) : null,
      p90Ms: s.p90Ms !== null ? Math.round(s.p90Ms) : null,
      sessionCount: s.sessionCount,
    })) ?? [];

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('styling.timingAnalysis', 'タイミング分析')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-tertiary)]">
            {t('styling.timingDescription', 'スタイリング作業のステップ別所要時間')}
          </p>
        </div>
        <DateRangeSelector
          preset={preset}
          onPresetChange={setPreset}
          startDate={startDate}
          endDate={endDate}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* 全体タイミング サマリ */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SimpleKpiCard
            label={t('styling.avgTime', '平均')}
            value={
              data.overallTiming.avgMs !== null ? formatDuration(data.overallTiming.avgMs) : '-'
            }
          />
          <SimpleKpiCard
            label={t('styling.p50Time', '中央値')}
            value={
              data.overallTiming.p50Ms !== null ? formatDuration(data.overallTiming.p50Ms) : '-'
            }
          />
          <SimpleKpiCard
            label={t('styling.p90Time', '90%ライン')}
            value={
              data.overallTiming.p90Ms !== null ? formatDuration(data.overallTiming.p90Ms) : '-'
            }
          />
        </div>
      )}

      {/* ステップ別タイミング BarChart */}
      {stepChartData.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            {t('styling.stepTiming', 'ステップ別タイミング')}
          </h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stepChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  type="number"
                  tickFormatter={(v: number) => formatDuration(v)}
                  tick={{ fontSize: 11 }}
                />
                <YAxis type="category" dataKey="step" width={120} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text-primary)',
                  }}
                  formatter={
                    ((value: unknown, name: string) => [
                      typeof value === 'number' ? formatDuration(value) : '-',
                      name === 'avgMs' ? '平均' : name === 'p50Ms' ? '中央値' : '90%ライン',
                    ]) as never
                  }
                />
                <Bar dataKey="avgMs" name="平均" fill="var(--chart-1)" radius={[0, 4, 4, 0]} />
                <Bar dataKey="p50Ms" name="中央値" fill="var(--chart-2)" radius={[0, 4, 4, 0]} />
                <Bar dataKey="p90Ms" name="90%ライン" fill="var(--chart-3)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* 日次平均スタイリング時間トレンド */}
      {data && data.dailyTrend.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            {t('styling.dailyStylingTime', '日次平均スタイリング時間')}
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v: number) => formatDuration(v)} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text-primary)',
                  }}
                  formatter={
                    ((value: unknown, name: string) => [
                      typeof value === 'number' ? formatDuration(value) : '-',
                      name === 'avgMs' ? '平均' : name === 'p50Ms' ? '中央値' : '90%ライン',
                    ]) as never
                  }
                  labelFormatter={formatDate as never}
                />
                <Area
                  type="monotone"
                  dataKey="avgMs"
                  name="avgMs"
                  stroke="var(--chart-1)"
                  fill="var(--chart-1)"
                  fillOpacity={0.1}
                />
                <Area
                  type="monotone"
                  dataKey="p50Ms"
                  name="p50Ms"
                  stroke="var(--chart-2)"
                  fill="var(--chart-2)"
                  fillOpacity={0.1}
                />
                <Area
                  type="monotone"
                  dataKey="p90Ms"
                  name="p90Ms"
                  stroke="var(--chart-3)"
                  fill="var(--chart-3)"
                  fillOpacity={0.1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ステップ詳細テーブル */}
      {data && data.steps.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            {t('styling.stepDetail', 'ステップ詳細')}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-2 px-3 text-[var(--color-text-secondary)]">
                    ステップ
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    セッション数
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">平均</th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    中央値
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    90%ライン
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.steps.map((s) => (
                  <tr
                    key={s.step}
                    className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
                  >
                    <td className="py-2 px-3 text-[var(--color-text-primary)]">
                      {STEP_LABELS[s.step] ?? s.step}
                    </td>
                    <td className="text-right py-2 px-3 font-mono">
                      {s.sessionCount.toLocaleString()}
                    </td>
                    <td className="text-right py-2 px-3 font-mono">
                      {s.avgMs !== null ? formatDuration(s.avgMs) : '-'}
                    </td>
                    <td className="text-right py-2 px-3 font-mono">
                      {s.p50Ms !== null ? formatDuration(s.p50Ms) : '-'}
                    </td>
                    <td className="text-right py-2 px-3 font-mono">
                      {s.p90Ms !== null ? formatDuration(s.p90Ms) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** @graph-connects none */
function SimpleKpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="card animate-fade-in-up">
      <p className="text-xs text-[var(--color-text-muted)] mb-1">{label}</p>
      <p className="text-2xl font-bold text-[var(--color-text-primary)]">{value}</p>
    </div>
  );
}
